import { DatePicker } from '@mui/lab';
import { Grid } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DocumentDigital } from 'shell/view-models/act-form';
import { memo, useEffect } from 'react';
import MunicipalityList from 'shell/components/shared/municipality-list';
import PmiTextField from 'shell/components/shared/text-field';

interface FormDigitalProps {
  values?: DocumentDigital;
  onChange: (key: keyof DocumentDigital, value: DocumentDigital[keyof DocumentDigital]) => void;
  errors?: FormikErrors<DocumentDigital>;
  touched?: FormikTouched<DocumentDigital>;
  onChangeFields: (values: DocumentDigital) => void;
  allDisabled?: boolean;
  isNotDisabledData?: boolean;
  required?: { [K in keyof DocumentDigital]: boolean };
}

const FormDigitalComponent = ({
  values,
  onChange,
  onChangeFields,
  errors,
  allDisabled,
  touched,
  required = {
    documentDate: true,
  },
  isNotDisabledData,
}: FormDigitalProps): React.ReactElement => {
  useEffect(() => {
    if (
      values?.year &&
      values?.nationalNumber &&
      values?.numberMunicipality &&
      values?.municipality?.id
    ) {
      onChange(
        'idAnsc',
        `${values?.year || ''}-${values?.nationalNumber}-${values?.numberMunicipality}-${
          values.municipality?.id
        }`
      );
    }
  }, [values?.year, values?.nationalNumber, values?.numberMunicipality, values?.municipality?.id]);

  return (
    <Grid container spacing={1}>
      <Grid item spacing={0} container xs={12}>
        <Grid item xs={12} md={4}>
          <DatePicker
            label={required?.documentDate ? 'Data atto *' : 'Data atto'}
            aria-label={required?.documentDate ? 'Data atto *' : 'Data atto'}
            value={values?.documentDate || null}
            onChange={(date) => {
              onChange('documentDate', date);
            }}
            disabled={isNotDisabledData ? false : allDisabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="documentDate"
                fullWidth
                error={touched?.documentDate && Boolean(errors?.documentDate)}
                helperText={errors?.documentDate}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          aria-label="Comune"
          label="Comune"
          id="municipality"
          required
          disabled={allDisabled}
          fromDate={values?.documentDate}
          value={values?.municipality || null}
          onChange={(municipality) => onChangeFields({ municipality, istatCode: municipality?.id })}
          error={touched?.municipality && Boolean(errors?.municipality)}
          helperText={errors?.municipality}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Provincia"
          label="Provincia"
          id="province"
          name="province"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.municipality?.province || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={1}>
        <PmiTextField
          aria-label="Anno"
          label="Anno *"
          id="year"
          name="year"
          fullWidth
          type="text"
          variant="outlined"
          inputProps={{
            maxLength: '4',
            pattern: '\\d{1,4}',
          }}
          disabled={allDisabled}
          value={values?.year || ''}
          onChange={(event) => onChange('year', event.target.value)}
          error={touched?.year && Boolean(errors?.year)}
          helperText={errors?.year}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Numero nazionale *"
          label="Numero nazionale *"
          id="nationalNumber"
          name="nationalNumber"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.nationalNumber || ''}
          onChange={(event) => {
            onChange('nationalNumber', event.target.value);
          }}
          inputProps={{
            maxLength: '20',
            pattern: '\\d{1,20}',
          }}
          error={touched?.nationalNumber && Boolean(errors?.nationalNumber)}
          helperText={errors?.nationalNumber}
          disabled={allDisabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Numero comunale *"
          label="Numero comunale *"
          id="numberMunicipality"
          name="numberMunicipality"
          fullWidth
          disabled={allDisabled}
          type="text"
          inputProps={{
            maxLength: '5',
            pattern: '\\d{1,5}',
          }}
          variant="outlined"
          value={values?.numberMunicipality || ''}
          onChange={(event) => onChange('numberMunicipality', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          aria-label="ID ANSC *"
          label="ID ANSC *"
          id="idAnsc"
          name="idAnsc"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.idAnsc || ''}
          onChange={(event) => {
            const idAnsc = event.target.value;

            let year, numberMunicipality, istatCode;

            if (idAnsc) {
              const [yearAnsc, , numberMunicipalityAnsc, istatCodeAnsc] = idAnsc.split('-');
              year = yearAnsc;
              numberMunicipality = numberMunicipalityAnsc;
              istatCode = istatCodeAnsc;
            }

            onChangeFields({ idAnsc, year, numberMunicipality, istatCode });
          }}
          error={touched?.idAnsc && Boolean(errors?.idAnsc)}
          helperText={errors?.idAnsc}
          disabled
        />
      </Grid>
    </Grid>
  );
};

FormDigitalComponent.displayName = 'Form Digital component';

const FormDigital = memo(FormDigitalComponent);

export default FormDigital;
